import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { targetsApi, formatApiError } from '../lib/api';
import { Link } from 'react-router-dom';
import {
  Loader2,
  AlertTriangle,
  Check,
  Merge,
  Trash2,
  RefreshCw,
  CheckCircle
} from 'lucide-react';

const DeduplicationPage = () => {
  const [duplicates, setDuplicates] = useState([]);
  const [totalTargets, setTotalTargets] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [merging, setMerging] = useState(null);
  const [merged, setMerged] = useState(new Set());

  const scanDuplicates = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await targetsApi.findDuplicates();
      setDuplicates(res.data.duplicates || []);
      setTotalTargets(res.data.total_targets || 0);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    scanDuplicates();
  }, []);

  const handleMerge = async (keepId, removeId, pairIndex) => {
    setMerging(pairIndex);
    try {
      await targetsApi.merge(keepId, removeId);
      setMerged(prev => new Set([...prev, pairIndex]));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setMerging(null);
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence === 'high') return 'text-red-400 bg-red-500/10 border-red-500/30';
    if (confidence === 'medium') return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
    return 'text-[#A3B5C7] bg-white/5 border-white/10';
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-[#00D4C8] animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl text-white mb-2" data-testid="dedup-title">Target Deduplication</h1>
          <p className="text-[#A3B5C7] text-sm">
            Scanned {totalTargets} targets — found {duplicates.length} potential duplicate pair{duplicates.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={scanDuplicates}
          className="btn-secondary flex items-center gap-2"
          data-testid="rescan-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Rescan
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
          {error}
        </div>
      )}

      {duplicates.length === 0 ? (
        <div className="card-surface p-12 text-center">
          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h3 className="font-heading text-xl text-white mb-2">No Duplicates Found</h3>
          <p className="text-[#A3B5C7]">Your target database is clean.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {duplicates.map((pair, index) => {
            const isMerged = merged.has(index);
            const [t1, t2] = pair.targets;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isMerged ? 0.4 : 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`card-surface p-6 ${isMerged ? 'pointer-events-none' : ''}`}
                data-testid={`duplicate-pair-${index}`}
              >
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  <span className={`text-xs uppercase tracking-wide px-2 py-0.5 rounded border ${getConfidenceColor(pair.confidence)}`}>
                    {pair.confidence} confidence
                  </span>
                  <span className="text-xs text-[#A3B5C7]">{pair.reason}</span>
                  {isMerged && (
                    <span className="text-xs text-green-400 ml-auto flex items-center gap-1">
                      <Check className="w-3 h-3" /> Merged
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[t1, t2].map((t, ti) => (
                    <div key={ti} className="p-4 rounded-lg bg-[#0A1628] border border-[#1A2F4C]">
                      <Link
                        to={`/targets/${t.id}`}
                        className="text-white hover:text-[#00D4C8] font-medium transition-colors"
                      >
                        {t.name}
                      </Link>
                      <p className="text-xs text-[#A3B5C7] mt-1">{t.type}</p>
                      {t.hq_location && <p className="text-xs text-[#A3B5C7]">{t.hq_location}</p>}
                      {!isMerged && (
                        <button
                          onClick={() => handleMerge(t.id, (ti === 0 ? t2 : t1).id, index)}
                          disabled={merging === index}
                          className="mt-3 text-xs text-[#00D4C8] hover:text-white flex items-center gap-1 transition-colors"
                          data-testid={`keep-${index}-${ti}`}
                        >
                          {merging === index ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Check className="w-3 h-3" />
                          )}
                          Keep this one
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DeduplicationPage;
